// File: frontend/src/pages/KnowledgeGraph/components/StatsBar.tsx
import React from 'react';
import { Box, Paper, Typography, Skeleton, Stack, alpha } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import HubIcon from '@mui/icons-material/Hub';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VerifiedIcon from '@mui/icons-material/Verified';
import { KGStatsResponse } from '../types';
import { cyber } from '../../../theme';

interface StatCardProps {
  label: string;
  value: number | undefined;
  loading: boolean;
  icon: React.ReactNode;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, loading, icon, accent }) => (
  <Paper
    sx={{
      p: 2,
      flex: '1 1 0',
      minWidth: 140,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      border: `1px solid ${cyber.border}`,
      boxShadow: 'none',
      transition: 'border-color 0.25s ease',
      '&:hover': { borderColor: alpha(accent, 0.5) },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: alpha(accent, 0.12),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: accent,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      {loading ? (
        <Skeleton width={60} height={28} />
      ) : (
        <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: cyber.fontMono, color: accent }}>
          {value?.toLocaleString() ?? '—'}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

interface StatsBarProps {
  stats: KGStatsResponse | null;
  loading: boolean;
}

const StatsBar: React.FC<StatsBarProps> = ({ stats, loading }) => {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        mb: 3,
        overflowX: 'auto',
        pb: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
      }}
    >
      <StatCard label="Reports Ingested" value={stats?.reports} loading={loading} icon={<ArticleIcon fontSize="small" />} accent={cyber.primaryLight} />
      <StatCard label="Canonical Entities" value={stats?.canon_entities} loading={loading} icon={<HubIcon fontSize="small" />} accent={cyber.accent.cyan} />
      <StatCard label="Cross-Report Relations" value={stats?.observed_rel} loading={loading} icon={<DeviceHubIcon fontSize="small" />} accent={cyber.accent.green} />
      <StatCard label="Inferred Relations" value={stats?.inferred_rel_all} loading={loading} icon={<AutoFixHighIcon fontSize="small" />} accent={cyber.accent.coral} />
      <StatCard label="High Confidence" value={stats?.inferred_rel_high_conf} loading={loading} icon={<VerifiedIcon fontSize="small" />} accent={cyber.primaryLight} />
    </Stack>
  );
};

export default StatsBar;
